// Step5Final.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
// FIX: this used to import the plain npm bootstrap.min.css directly.
// index.js already loads the theme's own compiled Bootstrap
// (vendors.min.css/app.min.css via <link> tags in public/index.html);
// webpack injects a component-level CSS import like this one AFTER those
// <link> tags, so unthemed default-Bootstrap rules were silently winning
// and overriding the theme's styling on this page specifically (same bug
// already fixed once in src/index.js -- see the comment there).
import { api as axios, authFetch } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";
import { ROUTES } from "../config/routes";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// FIX: confirmed against your real app.py's blueprint registrations
// (the bottom "REGISTER ALL BLUEPRINTS" section). Your blueprints do NOT
// all share one prefix:
//   app.register_blueprint(cost_estimate_api, url_prefix="/api")  -> /api/cost_estimate_api
//   app.register_blueprint(factors_api, url_prefix="/api")         -> /api/get_cost_factors
//   app.register_blueprint(projects_api)                          -> /projects/status  (NO /api)
// So this page needs two different base URLs, not one. Both now derive
// from the single API_ORIGIN in src/config/apiConfig.js.
const API_BASE = API_ORIGIN;   // for cost_estimate_api / factors_api (have /api prefix)
const API_ROOT = API_ORIGIN;             // for projects_api routes (no prefix)

// Same wizard-step indicator as every other Cost Estimation page.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Which of the 7 wizard steps this PAGE is. Steps before this one get
// the theme's own light-green "done" tint (.wizard-item-done -- built
// into app.min.css, so this part always rendered correctly); THIS step
// gets the solid brand-green "current" highlight (.wizard-item-current,
// defined in src/styles/themeOverrides.css -- see Layout.jsx for why
// that file, not appLayout.css, is what actually loads it); steps after
// this one stay inert (.disabled).
const CURRENT_STEP_KEY = "estimation";
const CURRENT_STEP_INDEX = WIZARD_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY);

// Factor row: label | % input | readonly amount box. FIX: this used to be
// defined INSIDE Step5Final() below, which meant React saw a brand new
// `FactorRow` function (a different component "type") on every re-render
// of the parent -- so every keystroke in one of these boxes (which
// triggers a re-render via setPrelims/setRisk/etc.) made React tear down
// and rebuild the underlying <input> DOM node from scratch, killing focus
// and the cursor position after each character typed (reported: typing
// "11.22" left the box on "11." with the cursor gone, requiring a
// re-click before every keystroke). Moving it to module scope like this
// gives it a stable identity across renders, so React just updates the
// existing input's value/props instead of remounting it -- typing now
// behaves like any other text box. Same fix applied to
// Step2RiskInflation.jsx's FactorRow.
//
// VAT is NOT one of these rows -- per request it lives in its own column
// in the Cost Allocation card below instead (select %, then see the
// computed amount next to it), not inside this Cost Factors (%) card.
const FactorRow = ({ label, value, onChange, amount, showAmount = true, fmt }) => (
  <div className="col-md-6">
    <div className="row align-items-center g-2">
      <label className="col-6 col-md-3 col-form-label">{label}</label>
      <div className={showAmount ? "col-6 col-md-4" : "col-12 col-md-9"}>
        <input
          type="text"
          inputMode="decimal"
          className="form-control"
          placeholder="0"
          value={value}
          onChange={onChange}
          onFocus={(e) => e.target.select()}
        />
      </div>
      {showAmount && (
        <div className="col-12 col-md-5">
          <input className="form-control bg-light" readOnly value={fmt(amount)} />
        </div>
      )}
    </div>
  </div>
);

export default function Step5Final() {
  const navigate = useNavigate();

  // Page-level header (below) shows "Project : <name>" instead of a
  // generic "Start Cost Estimation" title -- same change already made
  // on Step4SubElement.jsx. This page keeps project fields as plain
  // top-level localStorage keys (see Project_Name below, which already
  // reads localStorage.getItem("projectName")) rather than the
  // step3-object pattern Step4 uses, so this pulls the same key
  // straight into state instead of introducing a different pattern.
  const [projectName] = useState(() => localStorage.getItem("projectName") || "");

  // Duration: a real, independently-selectable dropdown here (per
  // request) -- unlike Step2RiskInflation.jsx's read-only display, this
  // page lets the user pick a different Duration_Master value.
  // Fix: this used to read a global (non-project-scoped) "duration"
  // localStorage key as its starting value -- the exact same class of
  // cross-project leakage bug already fixed for the Design/Supervision/
  // Other Cost/Factor (%) fields below (see the removed draft-restore
  // block further down) and for Step2RiskInflation.jsx's own Duration
  // dropdown. A brand-new project with nothing saved yet would silently
  // inherit whatever duration some OTHER project last had. Always starts
  // blank now; the per-project fetch effect below fills in the real
  // saved value (or leaves it blank for a genuinely new estimation).
  const [duration, setDuration] = useState("");
  const [durations, setDurations] = useState([]);

  // Currency symbol for the "Estimated (...)" / "Cost (...)" column
  // headers below -- fetched from the Currency Master table (GET
  // /currency, same endpoint Step4SubElement.jsx already uses) instead
  // of the hard-coded "SAR" literal, so changing that table's
  // Currency_Symbol value changes what shows here with no code change.
  // Starts blank rather than defaulting to a guessed symbol, so nothing
  // hard-coded ever briefly shows.
  const [currencySymbol, setCurrencySymbol] = useState("");

  useEffect(() => {
    axios
      .get(`${API_ROOT}/currency`)
      .then((res) => setCurrencySymbol(res.data?.data?.Currency_Symbol || ""))
      .catch((err) => console.error("Failed to load currency", err));
  }, []);

  // Inputs
  const [estimated, setEstimated] = useState(0);
  const [design, setDesign] = useState(0);
  const [supervision, setSupervision] = useState(0);
  const [otherProject, setOtherProject] = useState(0);

  // Percents -- per request, each Factor (%) field is now a free-text
  // input (see FactorRow below) instead of a "Select..." dropdown, and
  // defaults to "0.00" rather than blank. `safe()` (used everywhere these
  // feed a calculation) already treats "" as 0, so this default is purely
  // a display convenience -- it doesn't change any total until the user
  // actually types a different value.
  const [prelims, setPrelims] = useState("0.00");
  const [risk, setRisk] = useState("0.00");
  const [inflation, setInflation] = useState("0.00");
  const [contingency, setContingency] = useState("0.00");
  const [location, setLocation] = useState("0.00");
  const [tender, setTender] = useState("0.00");
  const [escalation, setEscalation] = useState("0.00");
  // VAT (%) is a dropdown again, per request -- same idea as the other
  // Factor dropdowns, just backed by its own dedicated Vat_Config table
  // (see GET /vat-config / vat_config_dropdown_migration.sql) instead of
  // living inside Cost_Factors. Starts at 15 as a safe fallback in case
  // the fetch below is slow or fails, but that effect overwrites it with
  // whichever option is flagged Is_Default on load, and a project's own
  // previously-saved VAT_Percent still wins over either -- see
  // projectVatLoadedRef below.
  const [vat, setVat] = useState(15);
  const [vatOptions, setVatOptions] = useState([]);

  // Set to true as soon as a real VAT value for THIS project is applied
  // (from a local draft or the project's saved estimation, below), so the
  // VAT-config default fetch never clobbers it if that fetch happens to
  // resolve after this one.
  const projectVatLoadedRef = useRef(false);

  useEffect(() => {
    axios
      .get(`${API_ROOT}/vat-config`)
      .then((res) => {
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        setVatOptions(rows);
        if (rows.length && !projectVatLoadedRef.current) {
          const def = rows.find((r) => r.Is_Default) || rows[0];
          setVat(Number(def.VAT_Percent));
        }
      })
      .catch((err) => console.error("Failed to load VAT config", err));
  }, []);

  // Calculated
  const [factorCost, setFactorCost] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [vatAmount, setVatAmount] = useState(0);
  const [totalSum, setTotalSum] = useState(0);

  // UI
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  // Success banner for the standalone Save button (see handleSaveOnly
  // below) -- "Finish" never needed one of its own since a successful
  // Finish opens the "add Basis of Cost?" confirm modal instead.
  const [saveMessage, setSaveMessage] = useState(null);

  const [lastSavedInfo, setLastSavedInfo] = useState(null);
  const [showFactorDetail, setShowFactorDetail] = useState(false); // NEW: for "View" toggle on Factor Cost

  // Modal state for YES/NO after save
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [postSaveNavigateTo, setPostSaveNavigateTo] = useState(null); // "basis" or "summary"

  const safe = (v) => Number(v || 0);

  // Robust parse helper (used by reloadEstimated)
  const parseNumber = (v) => {
    if (v === null || v === undefined) return NaN;
    if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
    const cleaned = String(v).replace(/[,₨$€£\s]/g, "").trim();
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  };

  // Compute estimated from snapshot if available (fallbacks)
  const computeEstimatedFromSnapshot = (snapshot) => {
    if (!snapshot) return NaN;
    try {
      const subElements = snapshot.subElements ?? {};
      const selectedSubs = snapshot.selectedSubs ?? {};
      let total = 0;
      Object.keys(selectedSubs).forEach((mainKey) => {
        const sel = Array.isArray(selectedSubs[mainKey]) ? selectedSubs[mainKey] : [];
        const list = Array.isArray(subElements[mainKey]) ? subElements[mainKey] : [];
        list.forEach((se) => {
          if (sel.includes(se.SE_ID)) {
            const cost = Number(se.Cost ?? (Number(se.Rate ?? 0) * Number(se.Quantity ?? 0))) || 0;
            total += cost;
          }
        });
      });
      return total;
    } catch {
      return NaN;
    }
  };

  // Load estimated from localStorage with multiple fallbacks
  const loadEstimatedFromLocalStorage = useCallback(() => {
    const keysToTry = ["step5TotalCost", "step5_total", "step5Total", "step5TotalCostValue"];
    for (const k of keysToTry) {
      const raw = localStorage.getItem(k);
      const n = parseNumber(raw);
      if (!Number.isNaN(n) && n >= 0) return n;
    }

    const snapRaw = localStorage.getItem("step5_snapshot");
    if (snapRaw) {
      try {
        const snap = JSON.parse(snapRaw);
        const computed = computeEstimatedFromSnapshot(snap);
        if (!Number.isNaN(computed) && computed >= 0) return computed;
      } catch {}
    }

    const step5Raw = localStorage.getItem("step5");
    if (step5Raw) {
      try {
        const obj = JSON.parse(step5Raw);
        if (obj && (obj.total || obj.totalCost || obj.estimatedTotal)) {
          const n = parseNumber(obj.total ?? obj.totalCost ?? obj.estimatedTotal);
          if (!Number.isNaN(n)) return n;
        }
        if (obj && obj.subElements && obj.selectedSubs) {
          const computed = computeEstimatedFromSnapshot(obj);
          if (!Number.isNaN(computed) && computed >= 0) return computed;
        }
      } catch {}
    }

    // Generic fallback: search keys containing "step5" and "snapshot"
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (k.toLowerCase().includes("step5") && k.toLowerCase().includes("snapshot")) {
        try {
          const raw = localStorage.getItem(k);
          const snap = JSON.parse(raw);
          const computed = computeEstimatedFromSnapshot(snap);
          if (!Number.isNaN(computed) && computed >= 0) return computed;
        } catch {}
      }
    }

    return 0;
  }, []);

  // Load estimated on mount
  useEffect(() => {
    const est = loadEstimatedFromLocalStorage();
    setEstimated(Number.isFinite(est) ? est : 0);
  }, [loadEstimatedFromLocalStorage]);

  // Load Duration Master options on mount. GET /duration (duration_api) is
  // registered with NO url_prefix -- see the API_BASE/API_ROOT comment at
  // the top of this file -- so this uses API_ROOT, not API_BASE.
  useEffect(() => {
    axios
      .get(`${API_ROOT}/duration`)
      .then((res) => setDurations(res.data?.data || []))
      .catch((err) => console.error("Failed to load durations", err));
  }, []);

  // Prefill Design/Supervision/Other Cost/Factor (%) from the project's
  // previously-saved Flag='E' row, if any. "estimated" is not touched
  // here -- it always comes from Step 3/4 (see above).
  //
  // FIX: this used to check a global "step5Values" localStorage draft
  // FIRST and, if present, apply it and return before ever calling the
  // API below -- but that key isn't scoped per project, so it was
  // whatever was last saved on THIS PAGE for the last-used project,
  // regardless of which project is open now. Opening a brand-new project
  // (with no estimation of its own saved yet) silently inherited another
  // project's Design/Supervision/Other Cost/Factor (%) values (e.g.
  // "1,000.00"/"100.00"/"100"/5%) instead of the real 0.00 defaults.
  // Removed entirely -- every field now always starts at its true
  // 0/"0.00" default (see useState above) and is filled in only from
  // this project's own real saved estimation, if it has one.
  useEffect(() => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) return;
    axios
      .get(`${API_ROOT}/get_project_estimations_api?projectId=${encodeURIComponent(projectId)}`)
      .then((res) => {
        // Same fix as Step2RiskInflation.jsx's own Duration dropdown: the
        // saved value lives at the top level of this response ("duration",
        // a sibling of "estimated"/"budget" -- see get_project_estimations_api
        // in app.py), not on `est` below. Without this, reopening an
        // existing project's Step 5 always showed "Select" regardless of
        // what was actually saved, and saving without reselecting would
        // blank out Cost_Budget_Estimation's Flag='E' row's Duration.
        if (res.data?.success && res.data?.duration) {
          setDuration(res.data.duration);
        }

        const est = res.data?.estimated;
        if (!res.data?.success || !est) return;
        if (est.Design !== undefined && est.Design !== null) setDesign(Number(est.Design) || 0);
        if (est.Supervision !== undefined && est.Supervision !== null) setSupervision(Number(est.Supervision) || 0);
        if (est.Other_Project !== undefined && est.Other_Project !== null) setOtherProject(Number(est.Other_Project) || 0);
        // Factor (%) fields default to "0.00" (see useState above). A
        // saved value of exactly 0 here almost always means the field was
        // left at that default the last time this was saved (safe() below
        // already turns "0.00"/"" into 0 for the DB either way), so: only
        // overwrite the default when the saved percent is truthy (skips
        // undefined, null, NaN, AND 0) -- functionally a no-op either way
        // since 0 and "left at default" behave identically in every total.
        if (est.Prelims_Percent) setPrelims(String(est.Prelims_Percent));
        if (est.Risk_Percent) setRisk(String(est.Risk_Percent));
        if (est.Inflation_Percent) setInflation(String(est.Inflation_Percent));
        if (est.Contingency_Percent) setContingency(String(est.Contingency_Percent));
        if (est.Location_Percent) setLocation(String(est.Location_Percent));
        if (est.Tender_Percent) setTender(String(est.Tender_Percent));
        if (est.Escalation_Percent) setEscalation(String(est.Escalation_Percent));
        if (est.VAT_Percent !== undefined && est.VAT_Percent !== null) {
          projectVatLoadedRef.current = true;
          setVat(Number(est.VAT_Percent) || 0);
        }
      })
      .catch((err) => console.error("Failed to load saved project estimation for prefill:", err));
  }, []);

  // Listen for storage events (other tab)
  useEffect(() => {
    const onStorage = (e) => {
      if (!e || !e.key) return;
      if (e.key === "step5TotalCost" || e.key === "step5_snapshot" || e.key.toLowerCase().includes("step5")) {
        const est = loadEstimatedFromLocalStorage();
        setEstimated(Number.isFinite(est) ? est : 0);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [loadEstimatedFromLocalStorage]);

  // Manual reload (exposed in UI)
  const reloadEstimated = () => {
    const est = loadEstimatedFromLocalStorage();
    setEstimated(Number.isFinite(est) ? est : 0);
  };

  // Recalculate derived values whenever inputs change
  useEffect(() => {
    const base = safe(estimated);
    const p = (base * safe(prelims)) / 100;
    const r = (base * safe(risk)) / 100;
    const i = (base * safe(inflation)) / 100;
    const c = (base * safe(contingency)) / 100;
    const l = (base * safe(location)) / 100;
    const t = (base * safe(tender)) / 100;
    const e = (base * safe(escalation)) / 100;

    const factorTotal = p + r + i + c + l + t + e;
    setFactorCost(factorTotal);

    const sub = base + factorTotal + safe(design) + safe(supervision) + safe(otherProject);
    setSubTotal(sub);

    const vatCalc = (sub * safe(vat)) / 100;
    setVatAmount(vatCalc);

    setTotalSum(sub + vatCalc);
  }, [
    estimated,
    design,
    supervision,
    otherProject,
    prelims,
    risk,
    inflation,
    contingency,
    location,
    tender,
    escalation,
    vat
  ]);

  // Build payload for final save (Flag must be "E")
  const buildFinalPayload = () => ({
    Projectid: localStorage.getItem("projectID"),
    Project_Name: localStorage.getItem("projectName"),
    Estimation_Date: localStorage.getItem("estimationDate"),
    Estimated_by: localStorage.getItem("estimatedBy"),
    Operating_Company: localStorage.getItem("operatingCompany"),

    Project_Introduction: localStorage.getItem("intro") || "",
    Inflation_Forecast: localStorage.getItem("inflation") || "",
    risk: localStorage.getItem("risks") || "",
    Financial_Overview: localStorage.getItem("financial") || "",
    Contingency: localStorage.getItem("contingency") || "",
    Opportunities: localStorage.getItem("opportunities") || "",
    // Whatever the user picked in this page's own Duration dropdown above
    // (pre-filled from Step 1, but independently selectable here).
    Duration: duration || "",

    Estimated: safe(estimated),
    Budget: safe(estimated),
    Construction: safe(estimated),
    Design: safe(design),
    Supervision: safe(supervision),
    Other_Project: safe(otherProject),

    // safe()-wrapped so an unselected ("") Factor dropdown is sent to the
    // backend as 0 rather than an empty string -- these percents can now
    // start unselected (see the state declarations above), but the saved
    // record should still store a real number either way.
    Prelims_Percent: safe(prelims),
    Risk_Percent: safe(risk),
    Inflation_Percent: safe(inflation),
    Contingency_Percent: safe(contingency),
    Location_Percent: safe(location),
    Tender_Percent: safe(tender),
    Escalation_Percent: safe(escalation),
    VAT_Percent: safe(vat),

    Prelims_Amount: Number(((safe(estimated) * prelims) / 100).toFixed(2)),
    Risk_Amount: Number(((safe(estimated) * risk) / 100).toFixed(2)),
    Inflation_Amount: Number(((safe(estimated) * inflation) / 100).toFixed(2)),
    Contingency_Amount: Number(((safe(estimated) * contingency) / 100).toFixed(2)),
    Location_Amount: Number(((safe(estimated) * location) / 100).toFixed(2)),
    Tender_Amount: Number(((safe(estimated) * tender) / 100).toFixed(2)),
    Escalation_Amount: Number(((safe(estimated) * escalation) / 100).toFixed(2)),

    Factor_Cost: factorCost,
    Subtotal: subTotal,
    Vat_Amount: vatAmount,
    Total_Sum: totalSum,

    Flag: "E"
  });

  // Shared save request (Flag='E') -- extracted so both "Save" and
  // "Finish" run the exact same validate-then-POST logic below and only
  // differ in what happens afterward: Finish opens the "add Basis of
  // Cost?" confirm modal, Save just shows an inline success banner and
  // stays on the page (see handleSaveOnly / finishEstimation below).
  const saveFinalEstimationRequest = async () => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) {
      setServerError("Missing Project ID in localStorage. Please complete Step 1.");
      return { ok: false };
    }

    setSaving(true);
    setServerError(null);

    const payload = buildFinalPayload();

    try {
      const res = await authFetch(`${API_BASE}/cost_estimate_api`, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      // If server returns non-2xx, try to read JSON/text for message
      if (!res.ok) {
        let bodyText = "";
        try {
          const json = await res.json();
          bodyText = JSON.stringify(json);
        } catch (err) {
          bodyText = await res.text().catch(() => "<no body>");
        }
        throw new Error(`Server responded ${res.status}: ${bodyText}`);
      }

      const data = await res.json();
      if (data && data.success) {
        const savedInfo = {
          timestamp: new Date().toISOString(),
          id: data.id ?? null,
          message: data.message ?? null,
          totalSavedEstimated: payload.Estimated ?? null
        };
        setLastSavedInfo(savedInfo);

        try {
          localStorage.setItem("step5_final_saved", JSON.stringify({ payload, savedInfo }));
        } catch (err) {
          console.warn("Failed to persist step5 final snapshot", err);
        }

        return { ok: true, data, payload, savedInfo };
      }

      throw new Error("Server returned success=false or unexpected response: " + JSON.stringify(data));
    } catch (err) {
      console.error("Step 5 save error:", err);
      setServerError(err.message || "Unknown error while saving final estimation.");
      alert("Error saving final estimation");
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  // "Finish" -- save, then ask where to navigate next (unchanged behavior).
  const finishEstimation = async () => {
    setSaveMessage(null);
    const res = await saveFinalEstimationRequest();
    if (!res.ok) return;
    // Show custom modal instead of alert/confirm
    setShowConfirmModal(true);
    // default navigation if user chooses NO
    setPostSaveNavigateTo("summary");
  };

  // Per request: a standalone Save, ahead of Finish, on every Cost
  // Estimation wizard page -- saves without opening the "add Basis of
  // Cost?" modal or navigating anywhere.
  const handleSaveOnly = async () => {
    setSaveMessage(null);
    const res = await saveFinalEstimationRequest();
    if (res.ok) setSaveMessage("Estimation saved.");
  };

  // Modal handlers
  const handleModalNo = async () => {
    setShowConfirmModal(false);

    const projectid = localStorage.getItem("projectID");
    if (!projectid) {
      alert("Project ID missing.");
      return;
    }

    try {
      await axios.put(`${API_ROOT}/projects/status`, {
        projectid,
        status: "C"
      });

      navigate(ROUTES.COST_ESTIMATION_SUMMARY);
    } catch (err) {
      console.error("Status update failed:", err);
      alert("Failed to update project status.");
    }
  };

  const markProjectCompleted = async () => {
    const projectid = localStorage.getItem("projectID");
    if (!projectid) {
      alert("Project ID missing.");
      return;
    }

    try {
      await axios.put(`${API_ROOT}/projects/status`, {
        projectid,
        status: "C"
      });

      navigate(ROUTES.COST_ESTIMATION_SUMMARY);
    } catch (err) {
      console.error("Status update failed:", err);
      alert("Failed to update project status.");
    }
  };

  const handleModalYes = () => {
    setShowConfirmModal(false);
    navigate(ROUTES.BASIS_OF_COST);
  };

  const goBack = () => {
    // FIX: this used to navigate to "/cost-estimation-step5" — i.e. itself —
    // because it was written when this component thought it was "Step6"
    // going back to "Step5". Now that this component IS Step 5, BACK should
    // go to Step 4 instead. Adjust this route if your Step 4 path differs.
    navigate(ROUTES.COST_ESTIMATION_STEP4);
  };

  // Shared validator for the Design / Supervision / Other Project boxes:
  // digits with an optional single decimal point only. Anything else is
  // rejected with an alert and the value is left unchanged. The raw string
  // is kept in state (rather than coerced to Number immediately) so a
  // trailing "." isn't stripped mid-typing (e.g. entering "1.5") — every
  // place that uses these values already goes through safe()/Number().
  //
  // FIX: there was previously no cap on how many digits could be typed
  // here. An unbounded value flows straight into Sub Total / VAT / Total
  // Sum below, and once that sum reaches 1e21, JS's Number.toFixed(2)
  // (used by fmt()) silently switches to exponential notation instead of
  // a normal decimal string -- e.g. "6.666666666666666e+29" -- which is
  // exactly the runaway-value bug reported. Capping at 12 integer digits
  // and 2 decimal digits (up to 999,999,999,999.99 SAR) keeps every
  // downstream sum far below that threshold while still allowing any
  // realistic project cost.
  const MAX_COST_INT_DIGITS = 12;
  const MAX_COST_DECIMALS = 2;
  const COST_INPUT_PATTERN = new RegExp(
    `^\\d{0,${MAX_COST_INT_DIGITS}}(\\.\\d{0,${MAX_COST_DECIMALS}})?$`
  );

  const handleNumericCostChange = (setter) => (e) => {
    const val = e.target.value;
    if (val !== "" && !COST_INPUT_PATTERN.test(val)) {
      alert(`Enter a number with up to ${MAX_COST_INT_DIGITS} digits and ${MAX_COST_DECIMALS} decimal places.`);
      return;
    }
    setter(val);
  };

  // Chart Data
  const chartData = {
    // Per explicit follow-up request, this bar's label reads "Execution"
    // (matching Step2RiskInflation.jsx's chart, and superseding the
    // earlier "Estimated" label) -- safe(estimated) below is unchanged,
    // just displayed under this label.
    labels: [
      "Execution",
      "Design",
      "Supervision",
      "Other Project Cost",
      "Prelims",
      "Risk",
      "Inflation",
      "Contingency",
      "Location",
      "Tender Index",
      "Escalation"
    ],
    datasets: [
      {
        label: `Cost (${currencySymbol})`,
        data: [
          safe(estimated),
          safe(design),
          safe(supervision),
          safe(otherProject),
          (safe(estimated) * prelims) / 100,
          (safe(estimated) * risk) / 100,
          (safe(estimated) * inflation) / 100,
          (safe(estimated) * contingency) / 100,
          (safe(estimated) * location) / 100,
          (safe(estimated) * tender) / 100,
          (safe(estimated) * escalation) / 100
        ],
        backgroundColor: "#0f4c5c",
        borderRadius: 3,
        barThickness: 24
      }
    ]
  };

  // Per explicit request, every value shown on this chart (bar labels and Y
  // axis ticks) reads in millions -- e.g. "1.36M" instead of
  // "1,360,000.00" -- matching the millions formatting already used on the
  // Sub Total/VAT/Total Sum tiles above the chart (fmtMillions below).
  const formatChartMillions = (v) =>
    `${(Number(v || 0) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;

  // Fix: the "Execution" bar (by far the largest value here, e.g. 55.89M
  // against everything else under 3M) was reaching all the way to the top
  // of the chart area with no headroom above it, so its value label --
  // drawn at bar.y - 6, i.e. just above the bar's own top -- landed
  // partially or fully above the visible canvas and got cut off/hidden
  // behind the axis area. Clamped here the same way
  // Step2RiskInflation.jsx's barValueLabelPlugin already does (this chart
  // never had that clamp, which is why only Step2's chart was safe from
  // this): if the label's natural position is above chartArea.top+12, pin
  // it there instead so it always stays inside the visible chart. Paired
  // with the y-axis suggestedMax headroom added below, which keeps the
  // tallest bar itself from touching the top edge in the first place.
  const barValueLabelPlugin = {
    id: "barValueLabel",
    afterDatasetsDraw(chart) {
      const { ctx, chartArea } = chart;
      chart.data.datasets.forEach((dataset, dsIndex) => {
        const meta = chart.getDatasetMeta(dsIndex);
        meta.data.forEach((bar, index) => {
          const value = Number(dataset.data[index] || 0);

          let y = bar.y - 6;
          const minY = chartArea.top + 12;
          if (y < minY) y = minY;

          ctx.save();
          ctx.fillStyle = "#333";
          ctx.font = "600 11px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(formatChartMillions(value), bar.x, y);
          ctx.restore();
        });
      });
    }
  };

  // Headroom above the tallest bar so its value label always has room to
  // render instead of being clipped against the top of the chart -- same
  // fix/pattern as Step2RiskInflation.jsx's chartMaxValue.
  const chartMaxValue = Math.max(...chartData.datasets[0].data, 1);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      // Per explicit request (same as Step2RiskInflation.jsx's Proposed
      // Cost Summary chart): X axis titled "Cost Breakdown (<symbol>)"
      // (the categories -- Estimated, Design, Supervision, etc), Y axis
      // titled "Cost (<symbol>)" (the value scale). <symbol> is the same
      // currencySymbol state already used for the "Cost (...)" labels
      // elsewhere on this page.
      x: {
        ticks: { font: { size: 11 }, maxRotation: 40, minRotation: 40 },
        grid: { display: false },
        title: { display: true, text: "Cost Breakdown", font: { size: 12, weight: "bold" } }
      },
      y: {
        suggestedMax: chartMaxValue * 1.2,
        ticks: { callback: (val) => formatChartMillions(val), font: { size: 11 } },
        grid: { color: "rgba(0,0,0,0.06)" },
        title: { display: true, text: `Cost (${currencySymbol})`, font: { size: 12, weight: "bold" } }
      }
    }
  };

  // Factor row (label + % input + readonly amount box) -- same theme
  // markup/layout as Step2RiskInflation.jsx's FactorRow (row.g-2 >
  // label.col-md-3 + input.col-md-4 + input.col-md-5), just fed by this
  // page's separate state variables instead of one `values` object.
  // Per request (same change already made on Step2RiskInflation.jsx), the
  // % dropdown was replaced with a free-text input -- the user can type
  // any percentage instead of being limited to whatever values happen to
  // exist in the Cost_Factors table. Same "digits with an optional single
  // decimal point" validation as the Design/Supervision/Other Cost fields
  // (handleNumericCostChange above), just capped at 3 integer digits / 2
  // decimals (0–999.99%) instead of a currency-sized range.
  const PERCENT_INPUT_PATTERN = /^\d{0,3}(\.\d{0,2})?$/;
  const handlePercentChange = (onChangeValue) => (e) => {
    const raw = e.target.value;
    if (raw !== "" && !PERCENT_INPUT_PATTERN.test(raw)) return;
    onChangeValue(raw);
  };

  // Per request (same change already made on Step2RiskInflation.jsx),
  // every displayed cost value now shows thousands separators
  // ("10,000.00" instead of "10000.00") -- was a plain toFixed(2).
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Per explicit request (same as Step2RiskInflation.jsx's Proposed Cost
  // Summary tiles): the Sub Total/VAT/Total Sum tiles in the "Estimated
  // Cost Summary" row show their value in millions with the currency
  // symbol (e.g. "﷼ 1.36M" instead of "1,360,000.00"). currencySymbol is
  // the same dynamic value (Currency Master table) already used for the
  // "Cost (...)" labels elsewhere on this page, not a hard-coded literal.
  // Only used for those 3 tiles -- every other amount on this page keeps
  // the existing full-number fmt(). Per a later request, the symbol now
  // leads the value (matching the Dashboard's Executive Summary tiles)
  // instead of trailing it.
  const fmtMillions = (n) =>
    `${currencySymbol ? `${currencySymbol} ` : ""}${(Number(n || 0) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;

  // Same conversion EstimationSummary.jsx and Step2RiskInflation.jsx use
  // to show Project Duration in years instead of the raw "36 Months" (or
  // "60 Months (5 Years)") string Duration_Master stores.
  const formatDurationYears = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "—";
    const str = String(raw).trim();
    if (!str) return "—";
    const parenYearsMatch = str.match(/\((\d+(\.\d+)?)\s*years?\)/i);
    if (parenYearsMatch) {
      const y = Number(parenYearsMatch[1]);
      return `${y} Year${y === 1 ? "" : "s"}`;
    }
    if (/year/i.test(str) && !/month/i.test(str)) return str;
    const match = str.match(/(\d+(\.\d+)?)/);
    if (!match) return str;
    const months = Number(match[1]);
    if (!months) return str;
    const years = months / 12;
    const roundedYears = Number.isInteger(years) ? years : Math.round(years * 10) / 10;
    return `${roundedYears} Year${roundedYears === 1 ? "" : "s"}`;
  };

  // Design / Supervision / Other Project are the only EDITABLE cost
  // fields on this page (their raw string state already lets a trailing
  // "." survive mid-typing -- see handleNumericCostChange above); every
  // other cost value here is a read-only computed display, which fmt()
  // above already comma-formats fine. Same "format on blur, plain while
  // focused" approach as Step2RiskInflation.jsx: formatting live on every
  // keystroke would fight typing decimals (Number() drops a trailing ".")
  // and reset the cursor to the end.
  const [focusedCostField, setFocusedCostField] = useState(null);
  const handleCostFocus = (field) => (e) => {
    const input = e.target;
    setFocusedCostField(field);
    // Deferred like Step2RiskInflation.jsx's fix -- selecting immediately
    // would select the still-comma-formatted text right before the
    // re-render (triggered by setFocusedCostField) swaps it out for the
    // plain value, collapsing the selection to nothing.
    setTimeout(() => input.select(), 0);
  };
  const handleCostBlur = () => setFocusedCostField(null);
  const displayCost = (field, raw) => (focusedCostField === field ? raw : fmt(raw));

  return (
    <>
      {/* Was a plain "Start Cost Estimation" title -- replaced with the
          actual project name in a white block, same as Step4SubElement.jsx. */}
      <div className="page-title-head d-flex align-items-center">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail instead of a
              plain bordered/uppercase title: "Cost Estimation /
              Estimation / Project : {name}", each non-current segment
              (and the "/" separators) in #788ca5 at 14px, normal case,
              no underline -- matching the reference design. The current
              segment ("Project : {name}") stays semibold in the app's
              brand teal so it still reads as the active/current item,
              same idea as the reference's blue "Portfolio" segment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {projectName || "—"}
            </span>
          </nav>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Wizard progress bar -- see the note above WIZARD_STEPS in
                  Step1ProjectInfo.jsx for why this is a fixed percentage
                  per page instead of the theme's JS-driven one. */}
              <div className="progress mb-4" style={{ height: 6 }}>
                <div className="progress-bar wizard-progress-bar" style={{ width: "71%" }} />
              </div>

              {/* Step indicator -- see the note above WIZARD_STEPS */}
              <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
                {WIZARD_STEPS.map((step, idx) => (
                  <li className="nav-item col-12 col-lg" key={step.key}>
                    <span
                      className={`nav-link h-100${
                        idx < CURRENT_STEP_INDEX
                          ? " wizard-item-done"
                          : idx === CURRENT_STEP_INDEX
                          ? " wizard-item-current"
                          : " disabled"
                      }`}
                    >
                      <span className="d-flex align-items-center">
                        {step.key === "budget" ? (
                          <svg
                            className="flex-shrink-0"
                            style={{ width: "32px", height: "32px" }}
                            viewBox="0 0 917 1025"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                          </svg>
                        ) : (
                          <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                        )}
                        <span className="flex-grow-1 ms-2 text-truncate">
                          <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                          <span className="fs-xxs mb-0">{step.sub}</span>
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <hr className="mt-3" />

              {serverError && (
                <div className="alert alert-danger" role="alert">
                  <strong>Save error:</strong> {serverError}
                </div>
              )}

              {/* ===================== ESTIMATED COST ===================== */}
              <h5 className="text-green mb-3 fs-4 fw-bold">Estimated Cost</h5>

              <div className="row g-3 mb-4">
                <div className="col-xl-4">
                  <div className="card h-100 border">
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                        <span className="fw-bold">General Costs</span>
                        <span className="fw-bold">Cost ({currencySymbol})</span>
                      </div>

                      {/* Per request, shortened from "Auto populated item
                          selections." to just "Auto populated" -- still
                          gray (text-muted), still its own column next to
                          the field. */}
                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Execution Cost</label>
                        <div className="col-sm-5">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(estimated)} />
                        </div>
                        <div className="col-sm-3 ps-0">
                          <small className="text-muted" style={{ color: "#6c757d" }}>Auto populated</small>
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Design Cost</label>
                        <div className="col-sm-8">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            maxLength={15}
                            value={displayCost("Design", design)}
                            onChange={handleNumericCostChange(setDesign)}
                            onFocus={handleCostFocus("Design")}
                            onBlur={handleCostBlur}
                          />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Supervision Cost</label>
                        <div className="col-sm-8">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            maxLength={15}
                            value={displayCost("Supervision", supervision)}
                            onChange={handleNumericCostChange(setSupervision)}
                            onFocus={handleCostFocus("Supervision")}
                            onBlur={handleCostBlur}
                          />
                        </div>
                      </div>

                      <div className="row align-items-center mb-3">
                        <label className="col-sm-4 col-form-label">Other Cost</label>
                        <div className="col-sm-8">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            maxLength={15}
                            value={displayCost("Other_Project", otherProject)}
                            onChange={handleNumericCostChange(setOtherProject)}
                            onFocus={handleCostFocus("Other_Project")}
                            onBlur={handleCostBlur}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-8">
                  <div className="border card h-100">
                    <div className="card-body">
                      <div className="border-bottom fw-bold mb-3 pb-2">Cost Factors (%)</div>
                      <div className="row g-3">
                        {/* Real, independently-selectable Duration dropdown
                            (per the original comment above the `duration`
                            state) -- pre-filled from Step 1 but the user can
                            pick a different Duration_Master value here.
                            Unlike Step2RiskInflation.jsx's read-only
                            Duration display, this one stays a live select. */}
                        <div className="col-md-6">
                          <div className="row align-items-center g-2">
                            <label className="col-6 col-md-3 col-form-label">Duration</label>
                            <div className="col-12 col-md-9">
                              <select
                                className="form-select"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                              >
                                <option value="">Select</option>
                                {durations.map((d) => (
                                  <option key={d.D_ID} value={d.Duration}>
                                    {d.Duration}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        <FactorRow label="Contingency" value={contingency} onChange={handlePercentChange(setContingency)} amount={(safe(estimated) * contingency) / 100} fmt={fmt} />
                        <FactorRow label="Prelims" value={prelims} onChange={handlePercentChange(setPrelims)} amount={(safe(estimated) * prelims) / 100} fmt={fmt} />
                        <FactorRow label="Location" value={location} onChange={handlePercentChange(setLocation)} amount={(safe(estimated) * location) / 100} fmt={fmt} />
                        <FactorRow label="Risk" value={risk} onChange={handlePercentChange(setRisk)} amount={(safe(estimated) * risk) / 100} fmt={fmt} />
                        <FactorRow label="Tender Index" value={tender} onChange={handlePercentChange(setTender)} amount={(safe(estimated) * tender) / 100} fmt={fmt} />
                        <FactorRow label="Inflation" value={inflation} onChange={handlePercentChange(setInflation)} amount={(safe(estimated) * inflation) / 100} fmt={fmt} />
                        <FactorRow label="Escalation" value={escalation} onChange={handlePercentChange(setEscalation)} amount={(safe(estimated) * escalation) / 100} fmt={fmt} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===================== COST BREAKDOWN ===================== */}
              <h5 className="text-green mb-3 mt-4 fs-4 fw-bold">Cost Breakdown</h5>

              <div className="row g-3">
                <div className="col-xl-5">
                  <div className="card h-100 border">
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                        <span className="fw-bold">Cost Allocation</span>
                        <span className="fw-bold">Cost ({currencySymbol})</span>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Execution Cost</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(estimated)} />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Cost Factors</label>
                        <div className="col-sm-6">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(factorCost)} />
                        </div>
                        <div className="col-sm-2 text-end ps-0">
                          <button
                            type="button"
                            className="btn btn-link p-0 fw-semibold text-decoration-none"
                            onClick={() => setShowFactorDetail((s) => !s)}
                          >
                            {showFactorDetail ? "Hide" : "View"}
                          </button>
                        </div>
                      </div>

                      {showFactorDetail && (
                        <div className="rounded border bg-light-subtle p-2 mb-3 fs-13">
                          <div className="d-flex justify-content-between"><span>Prelims</span><span>{fmt((safe(estimated) * prelims) / 100)}</span></div>
                          <div className="d-flex justify-content-between"><span>Risk</span><span>{fmt((safe(estimated) * risk) / 100)}</span></div>
                          <div className="d-flex justify-content-between"><span>Inflation</span><span>{fmt((safe(estimated) * inflation) / 100)}</span></div>
                          <div className="d-flex justify-content-between"><span>Contingency</span><span>{fmt((safe(estimated) * contingency) / 100)}</span></div>
                          <div className="d-flex justify-content-between"><span>Location</span><span>{fmt((safe(estimated) * location) / 100)}</span></div>
                          <div className="d-flex justify-content-between"><span>Tender Index</span><span>{fmt((safe(estimated) * tender) / 100)}</span></div>
                          <div className="d-flex justify-content-between"><span>Escalation</span><span>{fmt((safe(estimated) * escalation) / 100)}</span></div>
                        </div>
                      )}

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Design Cost</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(design)} />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Supervision Cost</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(supervision)} />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Other Cost</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(otherProject)} />
                        </div>
                      </div>

                      <hr className="my-3" />

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label fw-bold">Sub Total</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light fw-bold" readOnly value={fmt(subTotal)} />
                        </div>
                      </div>

                      {/* Per request, VAT is no longer inside the Cost
                          Factors (%) card above -- it's its own column
                          here instead: user picks the % first (dropdown,
                          sourced from Vat_Config via vatOptions), then the
                          computed amount shows next to it. */}
                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label fw-bold">VAT</label>
                        <div className="col-sm-4">
                          <select
                            className="form-select fw-bold"
                            value={vat}
                            onChange={(e) => setVat(Number(e.target.value))}
                          >
                            {vatOptions.map((opt) => (
                              <option key={opt.VAT_ID} value={opt.VAT_Percent}>
                                {opt.VAT_Percent}%
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-sm-4">
                          <input type="text" className="form-control bg-light fw-bold" readOnly value={fmt(vatAmount)} />
                        </div>
                      </div>

                      <div className="row align-items-center">
                        <label className="col-sm-4 col-form-label fw-bold">Total Sum</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light fw-bold" readOnly value={fmt(totalSum)} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-7">
                  <div className="card h-100 border">
                    <div className="card-body">
                      <h6 className="fw-bold mb-3">Estimated Cost Summary</h6>

                      <div className="row g-2 mb-3">
                        {/* Project Duration leads the tile row (matching
                            Step2RiskInflation.jsx's Proposed Cost Summary),
                            in a distinct light-blue so it reads as
                            informational rather than another cost figure --
                            in Years rather than the raw "36 Months" string. */}
                        <div className="col-3">
                          <div className="rounded text-center text-white p-2" style={{ background: "#4aa3df" }}>
                            <div className="fw-bold fs-18">{formatDurationYears(duration)}</div>
                            <div className="fs-12">Project Duration</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="rounded text-center text-white p-2" style={{ background: "#8b7fd4" }}>
                            <div className="fw-bold fs-18">{fmtMillions(subTotal)}</div>
                            <div className="fs-12">Sub Total</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="rounded text-center text-white p-2" style={{ background: "#17a76b" }}>
                            <div className="fw-bold fs-18">{fmtMillions(vatAmount)}</div>
                            <div className="fs-12">VAT</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="rounded text-center text-white p-2" style={{ background: "#eda100" }}>
                            <div className="fw-bold fs-18">{fmtMillions(totalSum)}</div>
                            <div className="fs-12">Total Sum</div>
                          </div>
                        </div>
                      </div>

                      {/* Matches Step2RiskInflation.jsx's Proposed Cost
                          Summary chart height, per explicit request. */}
                      <div style={{ height: 430 }}>
                        <Bar data={chartData} options={chartOptions} plugins={[barValueLabelPlugin]} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-sm-row justify-content-sm-between gap-2 mt-4">
                <button type="button" className="btn btn-outline-secondary" onClick={goBack} disabled={saving}>
                  ← Back
                </button>
                <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                  {lastSavedInfo && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() =>
                        alert(
                          `Last saved: ${lastSavedInfo.timestamp}\nID: ${lastSavedInfo.id}\nTotal Estimated Saved: ${lastSavedInfo.totalSavedEstimated}`
                        )
                      }
                    >
                      Last saved {new Date(lastSavedInfo.timestamp).toLocaleTimeString()}
                    </button>
                  )}
                  {/* Per request: a standalone Save, ahead of Finish, on
                      every Cost Estimation wizard page -- saves without
                      opening the "add Basis of Cost?" modal below. */}
                  <button type="button" className="btn btn-outline-primary" onClick={handleSaveOnly} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={finishEstimation} disabled={saving}>
                    {saving ? "Saving..." : "Finish"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Modal -- same theme markup as cost-estimation.html's
          #basisOfCostModal, driven by React state (this app doesn't load
          Bootstrap's JS -- see Header.jsx's dropdown-positioning comment --
          so there's no data-bs-toggle wiring here, just a plain overlay). */}
      {showConfirmModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="border-0 modal-content text-white" style={{ backgroundColor: "#124d52", borderRadius: 4 }}>
              <div className="text-center modal-body p-4">
                <div className="mb-3">
                  <div className="align-items-center justify-content-center bg-white d-inline-flex rounded-circle" style={{ width: 48, height: 48 }}>
                    <i className="fs-2 text-dark ti ti-alert-circle" style={{ fontStyle: "normal", fontWeight: 700 }} />
                  </div>
                </div>
                <h5 className="fs-5 fw-normal mb-4 modal-title">Do you want add basis of Cost Estimation?</h5>
                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn btn-primary" onClick={handleModalNo}>
                    No
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleModalYes}>
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}